import DOMPurify from "isomorphic-dompurify"

/**
 * Sanitizes HTML content to prevent XSS attacks.
 *
 * Uses DOMPurify (isomorphic-dompurify for Node.js/RSC context) with an
 * explicit allowlist configuration. Configured to block all known XSS vectors:
 *
 * Attack vectors tested:
 * 1. <script> tags — classic script injection
 * 2. onerror and other on* event handler attributes
 * 3. javascript: protocol in href attributes
 * 4. CSS expression / javascript: in style attributes
 * 5. Nested tag bypass (double-encoded script)
 * 6. SVG onload event injection
 * 7. HTML entity encoding bypass — handled natively by DOMPurify via DOM parser
 *
 * Allowlist includes common HTML formatting tags needed for legal page content.
 * Uses allowlist approach (not denylist) — denylists are inherently incomplete.
 *
 * Security: Adds rel="noopener noreferrer" to all target="_blank" links via
 * afterSanitizeAttributes hook to prevent reverse tabnapping attacks.
 *
 * Anti-pattern: NEVER use regex-based sanitization — always use a proven library.
 * Anti-pattern: NEVER pre-decode HTML entities before DOMPurify — DOMPurify's DOM
 *   parser handles entity encoding natively and pre-decoding can introduce new risks.
 */
// Register the afterSanitizeAttributes hook once at module level to prevent
// hook accumulation from repeated addHook/removeHook calls inside sanitizeHtml.
// DOMPurify hooks are global — registering inside a function that runs per-request
// creates a race condition under concurrent server-side rendering.
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  // Add rel="noopener noreferrer" to all target="_blank" links to prevent
  // reverse tabnapping (open redirect via window.opener).
  if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
    node.setAttribute("rel", "noopener noreferrer")
  }
})

export function sanitizeHtml(html: string): string {
  if (!html) return ""

  const result = DOMPurify.sanitize(html, {
    // CVE-2026-41238 mitigation (dompurify 3.0.1 to 3.3.3, isomorphic-dompurify ^3.3.0).
    // Without an explicit CUSTOM_ELEMENT_HANDLING option, DOMPurify falls back to
    // its default config object, which can be reached via Object.prototype on
    // engines where prototype-pollution has occurred earlier in the request.
    // A polluted tagNameCheck or attributeNameCheck regex is then used during
    // validation, allowing custom elements / attributes that bypass the allowlist.
    // Setting tagNameCheck=null and attributeNameCheck=null forces DOMPurify to
    // disallow any custom element or attribute outright, neutralising the bypass.
    // Reference: GHSA / CVE-2026-41238. Defense-in-depth alongside the dependency
    // upgrade tracked separately.
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: null,
      attributeNameCheck: null,
      allowCustomizedBuiltInElements: false,
    },
    // Allowlist of safe tags for content pages
    ALLOWED_TAGS: [
      // Text structure
      "p",
      "div",
      "span",
      "br",
      "hr",
      // Headings
      "h1",
      "h2",
      "h3",
      "h4",
      "h5",
      "h6",
      // Lists
      "ul",
      "ol",
      "li",
      // Inline formatting
      "a",
      "strong",
      "em",
      "b",
      "i",
      "u",
      "s",
      "code",
      "pre",
      // Tables
      "table",
      "thead",
      "tbody",
      "tfoot",
      "tr",
      "th",
      "td",
      // Media
      "img",
      // Semantic
      "blockquote",
      "cite",
      "dl",
      "dt",
      "dd",
      "sup",
      "sub",
    ],
    // Allowlist of safe attributes
    ALLOWED_ATTR: [
      "href",
      "src",
      "alt",
      "class",
      "id",
      "title",
      "width",
      "height",
      "target",
      "rel",
    ],
    // Force all URLs to be http:// or https:// (blocks javascript: URIs)
    ALLOW_DATA_ATTR: false,
    // Also strip text content inside forbidden tags (e.g., <script>alert(1)</script>)
    // This handles Attack Vector 5 (nested tag bypass) where text content may remain.
    FORBID_CONTENTS: ["script", "style", "iframe", "object", "embed", "form"],
  })

  return result
}
